import React from 'react';

// Helper function to generate unique keys
const createKey = (prefix: string, ...indices: (string | number)[]) => 
  `${prefix}-${indices.join('-')}`;
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Skeleton,
  Stack,
  Paper,
  LinearProgress,
} from '@mui/material';

interface LoadingSpinnerProps {
  message?: string;
  size?: number;
  variant?: 'spinner' | 'skeleton' | 'linear';
  minHeight?: string | number;
  fullScreen?: boolean;
}

/**
 * Enhanced loading component with multiple variants
 */
export function LoadingSpinner({ 
  message = 'Loading...', 
  size = 60,
  variant = 'spinner',
  minHeight = '400px',
  fullScreen = false,
}: LoadingSpinnerProps) {
  const containerProps = {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: fullScreen ? '100vh' : minHeight,
    gap: 2,
    ...(fullScreen && {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'background.default',
      zIndex: 9999,
    }),
  };

  if (variant === 'linear') {
    return (
      <Box sx={containerProps}>
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <LinearProgress />
          {message && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              align="center" 
              sx={{ mt: 2 }}
            >
              {message}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  if (variant === 'skeleton') {
    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Stack spacing={2}>
          <Skeleton variant="text" height={40} width="60%" />
          <Skeleton variant="rectangular" height={200} />
          <Stack direction="row" spacing={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" height={30} width="80%" />
              <Skeleton variant="text" height={20} width="60%" />
            </Box>
          </Stack>
          <Skeleton variant="rectangular" height={150} />
        </Stack>
      </Box>
    );
  }

  // Default spinner variant
  return (
    <Box sx={containerProps}>
      <CircularProgress 
        size={size} 
        thickness={4}
        sx={{
          color: 'primary.main',
          animationDuration: '550ms',
        }}
      />
      {message && (
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            maxWidth: 300,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 0.6 },
              '50%': { opacity: 1 },
              '100%': { opacity: 0.6 },
            },
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Inline loading component for smaller spaces
 */
export function InlineLoader({ 
  message, 
  size = 20 
}: { 
  message?: string; 
  size?: number; 
}) {
  return (
    <Box 
      display="flex" 
      alignItems="center" 
      gap={1} 
      sx={{ py: 1 }}
    >
      <CircularProgress size={size} thickness={4} />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Card skeleton for loading states in lists
 */
export function CardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <Stack spacing={2}>
      {Array.from({ length: count }, (_, index) => (
        <Paper key={index} sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Skeleton variant="text" width="40%" height={30} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </Box>
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="60%" height={20} />
            <Box display="flex" gap={1} mt={1}>
              <Skeleton variant="rectangular" width={60} height={32} />
              <Skeleton variant="rectangular" width={60} height={32} />
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

/**
 * Table skeleton for data table loading states
 */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box display="flex" gap={2} mb={2} px={2}>
        {Array.from({ length: columns }, (_, index) => (
          <Skeleton key={`header-${index}`} variant="text" width="100%" height={40} />
        ))}
      </Box>
      
      {/* Rows */}
      <Stack spacing={1}>
        {Array.from({ length: rows }, (_, rowIndex) => (
          <Box key={createKey('row', rowIndex)} display="flex" gap={2} px={2}>
            {Array.from({ length: columns }, (_, colIndex) => (
              <Skeleton 
                key={createKey('cell', rowIndex, colIndex)} 
                variant="text" 
                width="100%" 
                height={50} 
              />
            ))}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}